import React, { useEffect, useState } from 'react';
import { Payment, PaymentType } from '../../types';
import { CustomLoader } from '../utils/CustomLoader';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/MiscFunctions';
import { useAppContext } from '../apps/AppContext';
import { formatCurrency } from '../reports/components/ReportHelpers';
import { fetchPaymentsByTxnId, fetchPaymentsByReturnId, deletePaymentById } from '../../api';
import { CustomPrompt } from '../widgets/components/ModalPrompts';
import { useNavigate } from 'react-router-dom';

interface TxnPaymentsListProps {
  txnId: string;
  isReturn?: boolean;
  onAllocationDeleted?: () => void;
}

interface PaymentWithAllocations extends Payment {
  allocatedAmount?: number;
}

export const TxnPaymentsList: React.FC<TxnPaymentsListProps> = ({ txnId, isReturn, onAllocationDeleted }) => {
  const [payments, setPayments] = useState<PaymentWithAllocations[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<PaymentWithAllocations | null>(null);
  const { bizMaster } = useAppContext();
  const navigate = useNavigate();

  const currencySymbol = bizMaster?.bizDetails?.currency || '';

  useEffect(() => {
    if (txnId) {
      fetchPayments();
    }
  }, [txnId, isReturn]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      // A return's own allocation payment(s) are linked via paymentHeader.returnTxnId,
      // not paymentLines.txnId (that field points at whatever invoice the return was applied to).
      const paymentsData = isReturn
        ? await fetchPaymentsByReturnId(txnId)
        : await fetchPaymentsByTxnId(txnId);
      
      // Handle empty or null data
      if (!paymentsData || paymentsData.length === 0) {
        setPayments([]);
        return;
      }
      
      // Calculate allocated amount for each payment
      const paymentsWithAllocations = paymentsData.map(payment => ({
        ...payment,
        allocatedAmount: payment.paymentLines?.reduce((sum, line) => 
          sum + (line.allocAmt || 0), 0) || 0
      }));
      
      setPayments(paymentsWithAllocations || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch payments');
      console.error('Error fetching payments:', error);
      setPayments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteAllocation = async () => {
    const paymentId = paymentToDelete?.paymentHeader.paymentId;
    setPaymentToDelete(null);
    if (!paymentId) return;
    try {
      await deletePaymentById(paymentId);
      toast.success('Allocation deleted successfully!'.toTranslatedDisplay());
      await fetchPayments();
      onAllocationDeleted?.();
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to delete allocation.'.toTranslatedDisplay());
      console.error('Failed to delete allocation:', error);
    }
  };

  const handlePaymentClick = (payment: PaymentWithAllocations) => {
    const paymentType = payment.paymentHeader.paymentType || PaymentType.CustomerPayment;
    const paymentId = payment.paymentHeader.paymentId;
    navigate(`/transactions/view-payment/${paymentType}/${paymentId}`);
  };

  const getPaymentTypeLabel = (paymentType?: PaymentType) => {
    switch (paymentType) {
      case PaymentType.CustomerPayment:
        return 'Customer Payment';
      case PaymentType.VendorPayment:
        return 'Vendor Payment';
      case PaymentType.SalesReturnAllocation:
        return 'Sales Return';
      case PaymentType.PurchaseReturnAllocation:
        return 'Purchase Return';
      default:
        return 'Payment';
    }
  };

  const getStatusBadgeClass = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'badge-success';
      case 'partial':
        return 'badge-warning';
      case 'pending':
        return 'badge-info';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  if (loading) {
    return <CustomLoader />;
  }

  // Don't display anything if no payments found
  if (!payments.length) {
    return null;
  }

  return (
    <>
    <div className="card">
      <div className="card-header py-3">
        <div className="card-title">
          <h5 className="fw-bolder mb-0">
            <i className="bi bi-credit-card me-2"></i>
            {isReturn ? 'Allocations'.toTranslatedDisplay() : 'Payments'.toTranslatedDisplay()} ({payments.length})
          </h5>
        </div>
      </div>
      
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-sm table-row-gray-200 align-middle gs-0 gy-2">
            <thead>
              <tr className="fw-bold text-muted bg-light fs-7">
                <th className="ps-4 min-w-100px">{'Payment #'.toTranslatedDisplay()}</th>
                <th className="min-w-100px">{'Date'.toTranslatedDisplay()}</th>
                <th className="min-w-100px">{'Type'.toTranslatedDisplay()}</th>
                <th className="min-w-80px">{'Mode'.toTranslatedDisplay()}</th>
                <th className="min-w-80px text-end">{'Amount'.toTranslatedDisplay()}</th>
                <th className="min-w-80px text-end">{'Allocated'.toTranslatedDisplay()}</th>
                <th className="min-w-80px pe-4">{'Reference'.toTranslatedDisplay()}</th>
                {isReturn && <th className="w-50px pe-4"></th>}
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr 
                  key={payment.paymentHeader.paymentId} 
                  className="fs-7 cursor-pointer"
                  style={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f3f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => handlePaymentClick(payment)}
                  title={'Click to view payment details'.toTranslatedDisplay()}
                >
                  <td className="ps-4">
                    <span className="fw-bold d-block text-hover-primary">
                      {payment.paymentHeader.paymentNo || 'N/A'}
                    </span>
                  </td>
                  
                  <td>
                    <span className="fw-bold d-block">
                      {formatDate(payment.paymentHeader.paymentDate)}
                    </span>
                  </td>
                  
                  <td>
                    <span className="d-block">
                      {getPaymentTypeLabel(payment.paymentHeader.paymentType)}
                    </span>
                  </td>
                  
                  <td>
                    <span className="d-block">
                      {payment.paymentHeader.paymentMode || 'N/A'}
                    </span>
                  </td>
                  
                  <td className="text-end">
                    <span className="fw-bold d-block">
                      {formatCurrency(payment.paymentHeader.totalAmt, currencySymbol)}
                    </span>
                  </td>
                  
                  <td className="text-end">
                    <span className="fw-bold d-block">
                      {formatCurrency(payment.allocatedAmount || 0, currencySymbol)}
                    </span>
                  </td>
                  
                  <td className="pe-4">
                    <span className="text-muted d-block fs-8">
                      {payment.paymentHeader.reference || '-'}
                    </span>
                  </td>

                  {isReturn && (
                    <td className="pe-4 text-end">
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light-danger"
                        title={'Delete allocation'.toTranslatedDisplay()}
                        onClick={(e) => {
                          e.stopPropagation(); // don't trigger the row's navigate-to-payment click
                          setPaymentToDelete(payment);
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    {paymentToDelete && (
      <CustomPrompt
        isVisible
        onClose={() => setPaymentToDelete(null)}
        onConfirm={confirmDeleteAllocation}
        message={'Do you really want to delete this allocation? The amount will be released back to the return and the invoice balance will be restored.'.toTranslatedDisplay()}
        title={'Confirm Delete'.toTranslatedDisplay()}
        PrimaryText={'Delete'.toTranslatedDisplay()}
        secondaryText={'Cancel'.toTranslatedDisplay()}
      />
    )}
    </>
  );
};
